'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * HexGrid3D — 3D Hexagonal Grid mit Ripple-Wellen-Effekt
 *
 * Technik: CSS 3D transforms + framer-motion Ripple + clip-path Hexagone
 * CPU-Impact: Low (CSS transforms GPU-composited, event-driven Ripples)
 */

const HEX_CLIP = 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)';

function HexGrid3DBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  interactive = true,
  cols = 14,
  color,
  glowColor,
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const resolvedColor = color ?? (isDark ? '#06b6d4' : '#0284c7');
  const resolvedGlow = glowColor ?? (isDark ? '#8b5cf6' : '#7c3aed');
  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const containerRef = React.useRef(null);
  const [ripples, setRipples] = React.useState([]);
  const rippleIdRef = React.useRef(0);
  const [containerSize, setContainerSize] = React.useState({ w: 1200, h: 700 });

  // Track container dimensions
  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerSize({
        w: entry.contentRect.width || 1200,
        h: entry.contentRect.height || 700,
      });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Compute hex geometry from container size + cols
  const { hexes, hexW, hexH, dynRows } = React.useMemo(() => {
    const cW = containerSize.w;
    const cH = containerSize.h;
    // Fit exactly `cols` hexagons across the width
    const hexW = cW / (1 + 0.75 * (cols - 1));
    const hexH = hexW * (52 / 60);
    const offsetX = hexW * 0.75;
    // Fill height + 2 extra rows for safety (odd-col offset can push last row down)
    const dynRows = Math.ceil(cH / hexH) + 2;
    const list = [];
    for (let row = 0; row < dynRows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * offsetX;
        const y = row * hexH + (col % 2 === 1 ? hexH / 2 : 0);
        list.push({ id: `${col}-${row}`, col, row, x, y });
      }
    }
    return { hexes: list, hexW, hexH, dynRows };
  }, [containerSize, cols]);

  // Ambient ripple — fires periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      const col = Math.floor(Math.random() * cols);
      const row = Math.floor(Math.random() * dynRows);
      triggerRipple(col, row);
    }, (3000 + Math.random() * 2000) / speed);
    return () => clearInterval(interval);
  }, [cols, dynRows, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerRipple = React.useCallback((originCol, originRow) => {
    const id = rippleIdRef.current++;
    setRipples((prev) => [
      ...prev.slice(-3),
      { id, originCol, originRow, startTime: Date.now() },
    ]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 2500 / speed);
  }, [speed]);

  const handleClick = React.useCallback((e) => {
    if (!interactive) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const offsetX = hexW * 0.75;
    const col = Math.round(x / offsetX);
    const row = Math.round((y - (col % 2 === 1 ? hexH / 2 : 0)) / hexH);
    triggerRipple(
      Math.max(0, Math.min(cols - 1, col)),
      Math.max(0, Math.min(dynRows - 1, row)),
    );
  }, [interactive, cols, dynRows, hexW, hexH, triggerRipple]);

  return (
    <div
      data-slot="hex-grid-3d-background"
      className={cn('relative size-full overflow-hidden cursor-pointer', bgClass, className)}
      ref={containerRef}
      onClick={handleClick}
      style={{ perspective: '800px' }}
      {...props}
    >
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ opacity, transformStyle: 'preserve-3d' }}
        animate={{
          rotateX: [4, 6, 4],
          rotateY: [-2, 2, -2],
        }}
        transition={{
          duration: 12 / speed,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        {hexes.map((hex) => (
          <HexCell
            key={hex.id}
            hex={hex}
            ripples={ripples}
            color={resolvedColor}
            glowColor={resolvedGlow}
            speed={speed}
            hexW={hexW}
            hexH={hexH}
          />
        ))}
      </motion.div>

      {interactive && (
        <div className="absolute bottom-3 right-3 text-slate-600 text-xs pointer-events-none select-none">
          click to ripple
        </div>
      )}

      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

function HexCell({ hex, ripples, color, glowColor, speed, hexW, hexH }) {
  const rippleState = React.useMemo(() => {
    let maxInfluence = 0;
    for (const ripple of ripples) {
      const dist = Math.sqrt(
        (hex.col - ripple.originCol) ** 2 + (hex.row - ripple.originRow) ** 2,
      );
      const elapsed = (Date.now() - ripple.startTime) / 1000;
      const waveFront = elapsed * 4 * speed;
      const waveWidth = 2;
      const distFromFront = Math.abs(dist - waveFront);
      if (distFromFront < waveWidth) {
        const influence = Math.max(0, 1 - distFromFront / waveWidth);
        maxInfluence = Math.max(maxInfluence, influence);
      }
    }
    return maxInfluence;
  }, [hex, ripples, speed]);

  const hex2rgb = (h) => {
    const c = h.replace('#', '');
    return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
  };
  const [r, g, b] = hex2rgb(color);
  const [gr, gg, gb] = hex2rgb(glowColor);

  const baseOpacity = 0.06;
  const activeBorderOpacity = baseOpacity + rippleState * 0.5;
  const activeGlow = rippleState * 20;
  const translateZ = rippleState * 15;

  const cellW = hexW * 0.93;
  const cellH = hexH * 0.92;

  return (
    <div
      style={{
        position: 'absolute',
        left: hex.x,
        top: hex.y,
        width: cellW,
        height: cellH,
        clipPath: HEX_CLIP,
        background: rippleState > 0.1
          ? `rgba(${gr},${gg},${gb},${rippleState * 0.15})`
          : `rgba(${r},${g},${b},0.04)`,
        border: `1px solid rgba(${r},${g},${b},${activeBorderOpacity})`,
        boxShadow: activeGlow > 1 ? `0 0 ${activeGlow}px rgba(${gr},${gg},${gb},${rippleState * 0.4})` : 'none',
        transform: `translateZ(${translateZ}px)`,
        transition: 'transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
      }}
    />
  );
}

export { HexGrid3DBackground };
